import { FC } from 'react'

import { Box, LinearProgress, SxProps, Theme, Typography } from '@mui/material'

import { barOpacityForPct } from '../../helpers/relativeMetric'

interface RelativeMetricBarProps {
  /** Already-scaled 0-100 percentage (see `relativePct`). Drives bar width and opacity. */
  pct: number
  /**
   * Trailing readout. Defaults to `pct` rounded to a whole percent, which is
   * what most callers want; pass it explicitly when the label shows a different
   * figure than the bar (e.g. an absolute share next to a relative bar).
   */
  label?: string
  /** Bar thickness. Table cells use the default; the denser card grid uses 8. */
  height?: number
  /** Reserved width for the readout so columns of numbers stay aligned. */
  labelMinWidth?: number
  labelAlign?: 'left' | 'right'
  /** Merged into the container, for per-call spacing / min-width. */
  sx?: SxProps<Theme>
}

/**
 * Bar + tabular-nums readout for a value scaled against the strongest row in
 * the current view. Shared by the creative-ads table, the game creative-tags
 * table and the ad card grid so the opacity ramp and pill geometry stay in sync
 * across all three.
 */
export const RelativeMetricBar: FC<RelativeMetricBarProps> = ({ pct, label, height = 10, labelMinWidth = 40, labelAlign = 'left', sx }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...sx }}>
    <LinearProgress
      variant="determinate"
      value={pct}
      sx={{
        flex: 1,
        height,
        borderRadius: 999,
        '& .MuiLinearProgress-bar': { borderRadius: 999, opacity: barOpacityForPct(pct) },
      }}
    />
    <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', minWidth: labelMinWidth, textAlign: labelAlign }}>
      {label ?? `${pct.toFixed(0)}%`}
    </Typography>
  </Box>
)
